(() => {
  'use strict';

  const MH = window.MemoHol || { routes: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    const els = $$('[data-reveal]:not(.is-revealed)');
    if (!els.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) { els.forEach((el) => el.classList.add('is-revealed')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-revealed');
      io.unobserve(e.target);
    }), { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Hero video: muted autoplay on iOS / Android ----------
     The <source media> list picks the file (no src swapping). Muted is forced in JS too, then play() with errors
     swallowed (Low Power Mode / data saver reject it). The video fades in on "playing", or after 2s at the latest;
     until then the poster image underneath shows, so the hero is never blank. */
  function initHeroVideo() {
    // One <video> per screen size (data-hero-video = its media query, shown/hidden by CSS). Only the visible one plays
    // and loads; the other is paused and kept at preload="none". The file is fixed in the markup – JS never sets src.
    $$('[data-hero-video]').forEach((v) => {
      const mq = window.matchMedia(v.dataset.heroVideo && v.dataset.heroVideo !== 'all' ? v.dataset.heroVideo : 'all');
      const show = () => v.classList.add('is-on');
      const play = () => { v.muted = true; v.defaultMuted = true; v.setAttribute('muted', ''); if (v.paused) v.play().catch(() => {}); };
      const sync = () => {
        if (mq.matches) { if (v.preload !== 'auto') v.preload = 'auto'; play(); setTimeout(show, 2000); }
        else { v.pause(); v.classList.remove('is-on'); v.preload = 'none'; }
      };
      if (!v.dataset.heroBound) {
        v.dataset.heroBound = '1';
        v.addEventListener('playing', () => { if (mq.matches) show(); });
        v.addEventListener('canplay', () => { if (mq.matches) play(); });
        mq.addEventListener?.('change', sync);   // rotating across the breakpoint swaps which video plays
      }
      sync();
    });
  }

  /* ---------- Cart count + drawer ---------- */
  function setCartCount(count) {
    $$('[data-cart-count]').forEach((el) => { el.textContent = count; el.hidden = !count; });
  }
  document.addEventListener('cart:updated', (e) => {
    if (e.detail && typeof e.detail.count === 'number') setCartCount(e.detail.count);
  });

  function renderDrawer(html) {
    if (!html) return null;
    const next = parse(html).querySelector('[data-cart-drawer]');
    const drawer = $('[data-cart-drawer]');
    if (!next || !drawer) return null;
    $('[data-cart-drawer-body]', drawer).innerHTML = $('[data-cart-drawer-body]', next).innerHTML;
    drawer.dataset.count = next.dataset.count;
    return parseInt(next.dataset.count, 10);
  }

  let lastFocus = null;
  function openDrawer() {
    const drawer = $('[data-cart-drawer]');
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    $('.drawer__panel', drawer).focus();
  }
  function closeDrawer() {
    const drawer = $('[data-cart-drawer]');
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (lastFocus) lastFocus.focus();
  }
  document.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) closeDrawer(); });

  /* ---------- Quantity steppers (delegated) ---------- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
    if (!btn) return;
    const input = $('input', btn.closest('[data-qty]'));
    if (!input) return;
    const min = parseInt(input.min || '0', 10), max = parseInt(input.max || '99', 10);
    const cur = parseInt(input.value || '0', 10) || 0;
    const next = Math.max(min, Math.min(max, cur + (btn.hasAttribute('data-qty-plus') ? 1 : -1)));
    if (next === cur) return;
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const menuBtn = $('[data-menu-toggle]', header);
    const menu = $('#MobileMenu', header);
    const searchBtn = $('[data-search-toggle]', header);
    const panel = $('[data-search-panel]', header);
    const input = $('[data-search-input]', header);
    const results = $('[data-search-results]', header);
    const popular = $('[data-search-popular]', header);

    const setSearch = (open) => {
      if (!panel) return;
      panel.hidden = !open;
      searchBtn.setAttribute('aria-expanded', String(open));
      if (open) { setMenu(false); closeMegas(); setTimeout(() => input && input.focus(), 30); }
    };

    /* SHOP mega panel (desktop nav, ≥1120px): opens on hover and on keyboard focus, closes 180ms after the pointer
       leaves both the trigger and the panel, on Esc (focus back to the trigger) and on a click outside.
       aria-expanded follows. On touch screens wide enough for the desktop nav, the first tap opens the panel and
       a second tap follows the link. */
    const desk = window.matchMedia('(min-width: 1120px)');
    const megas = $$('[data-mega]', header).map((li) => {
      const trigger = $('[data-mega-trigger]', li), pane = document.getElementById(li.dataset.mega);
      if (!trigger || !pane) return null;
      let t = 0, hush = false, pointer = 'mouse';
      const set = (open) => { clearTimeout(t); pane.hidden = !open; trigger.setAttribute('aria-expanded', String(open)); };
      const open = () => {
        if (!desk.matches || hush) return;
        megas.forEach((m) => m && m.pane !== pane && m.set(false));
        if (panel && !panel.hidden) setSearch(false);
        set(true);
      };
      const later = () => { clearTimeout(t); t = setTimeout(() => set(false), 180); };
      // The panel is inside the <li>, so one hover / focus area covers the trigger, the bridge and the panel.
      li.addEventListener('mouseenter', open);
      li.addEventListener('mouseleave', later);
      li.addEventListener('focusin', open);
      li.addEventListener('focusout', (e) => { if (!li.contains(e.relatedTarget)) set(false); });
      trigger.addEventListener('pointerdown', (e) => { pointer = e.pointerType; });
      trigger.addEventListener('click', (e) => { if (desk.matches && pointer !== 'mouse' && pane.hidden) { e.preventDefault(); open(); } });
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || pane.hidden) return;
        const inside = li.contains(document.activeElement);
        set(false);
        if (inside) { hush = true; trigger.focus(); hush = false; }
      });
      document.addEventListener('pointerdown', (e) => { if (!pane.hidden && !li.contains(e.target)) set(false); });
      desk.addEventListener?.('change', () => set(false));
      return { pane, set };
    });
    function closeMegas() { megas.forEach((m) => m && m.set(false)); }
    // The open menu hangs under the sticky bar (CSS: absolute, max-height 100svh − bar, scrolls itself); the page
    // behind is locked (html only – see header.liquid), so nothing can slide over it.
    const setMenu = (open) => {
      if (!menu) return;
      menu.hidden = !open;
      menuBtn.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('menu-open', open);
    };
    menuBtn && menuBtn.addEventListener('click', () => { setMenu(menu.hidden); setSearch(false); });
    // The drawer never survives navigation: close it on a link tap, on load, when iOS Safari restores the page
    // from the back-forward cache (it would come back open, with the page scroll-locked and ✕ instead of ☰),
    // and on history steps (popstate).
    menu && menu.addEventListener('click', (e) => { if (e.target.closest('a[href]')) setMenu(false); });
    setMenu(false);
    window.addEventListener('pageshow', () => setMenu(false));
    window.addEventListener('popstate', () => setMenu(false));
    searchBtn && searchBtn.addEventListener('click', () => setSearch(panel.hidden));
    $('[data-search-close]', header)?.addEventListener('click', () => setSearch(false));
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (panel && !panel.hidden) { setSearch(false); searchBtn.focus(); }
      closeDrawer();
    });

    let controller;
    const search = debounce(async (q) => {
      if (!results) return;
      if (popular) popular.hidden = !!q;
      if (!q) { results.innerHTML = ''; return; }
      controller && controller.abort();
      controller = new AbortController();
      const url = `${MH.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&section_id=predictive-search`;
      try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(res.status);
        const doc = parse(await res.text());
        const sec = doc.querySelector('#shopify-section-predictive-search');
        results.innerHTML = sec ? sec.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') results.innerHTML = '';
      }
    }, 250);
    input && input.addEventListener('input', () => search(input.value.trim()));
    header.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-search-term]');
      if (!chip || !input) return;
      input.value = chip.dataset.searchTerm;
      search(input.value);
      input.focus();
    });

    // Touch devices get a brief label instead of the hover tooltip.
    header.closest('body').addEventListener('touchstart', (e) => {
      const tip = e.target.closest('[data-tip]');
      if (!tip) return;
      tip.classList.add('is-tipped');
      clearTimeout(tip._t);
      tip._t = setTimeout(() => tip.classList.remove('is-tipped'), 1400);
    }, { passive: true });

    // Homepage: the header's speech bubble follows the section in view.
    const spied = $$('main [data-nav]');
    if (document.body.classList.contains('template-index') && spied.length && 'IntersectionObserver' in window) {
      const links = $$('.nav-link[data-nav-index]', header);
      const setActive = (i) => links.forEach((a) => a.classList.toggle('is-active', a.dataset.navIndex === String(i)));
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.dataset.nav); });
      }, { rootMargin: '-40% 0px -59% 0px' });
      spied.forEach((s) => io.observe(s));
    }
  }

  /* ---------- Product form ---------- */
  /* ---------- Product: Back to shop. Coming from a Shop / collection page (same site) → history.back(), which
     keeps its filters and scroll position; otherwise follow the link (the collection, or all products). ---------- */
  function initBackToShop() {
    $$('[data-back-to-shop]').forEach((a) => a.addEventListener('click', (e) => {
      let ref;
      try { ref = new URL(document.referrer); } catch (err) { return; }
      if (ref.origin === location.origin && /\/collections\//.test(ref.pathname) && history.length > 1) { e.preventDefault(); history.back(); }
    }));
  }

  function initProduct(root) {
    const form = $('[data-product-form]', root);
    if (!form) return;
    const variants = JSON.parse(($('[data-variants]', root) || {}).textContent || '[]');
    const idInput = $('[data-variant-id]', form);
    const addBtns = $$('[data-add]', root);
    const errorEl = $('[data-form-error]', root);
    const fieldsets = $$('[data-options] fieldset', root);

    const showMedia = (id) => {
      if (!id) return;
      const slide = $(`.pdp__slide[data-media="${id}"]`, root);
      if (!slide) return;
      $$('.pdp__slide', root).forEach((s) => {
        const on = s === slide;
        s.classList.toggle('is-active', on);
        if (!on) $$('video', s).forEach((v) => v.pause());
        // The selected product video plays in the main box: muted, inline, with controls.
        if (on) $$('video', s).forEach((v) => { v.muted = true; v.playsInline = true; v.setAttribute('playsinline', ''); v.play().catch(() => {}); });
      });
      $$('[data-thumb]', root).forEach((t) => {
        const on = t.dataset.thumb === String(id);
        t.classList.toggle('is-active', on);
        on ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current');
      });
    };
    root.addEventListener('click', (e) => {
      const t = e.target.closest('[data-thumb]');
      if (t) showMedia(t.dataset.thumb);
    });

    const selected = () => fieldsets.map((fs) => ($('input:checked', fs) || {}).value);
    const markAvailability = (sel) => {
      fieldsets.forEach((fs, i) => {
        $$('input', fs).forEach((inp) => {
          const ok = variants.some((v) => v.available && v.options[i] === inp.value && v.options.every((o, j) => j === i || o === sel[j]));
          const label = inp.nextElementSibling;
          label && label.classList.toggle('is-unavailable', !ok);
        });
      });
    };
    const setButtons = (variant) => {
      addBtns.forEach((b) => {
        b.disabled = !variant || !variant.available;
        const l = $('[data-add-label]', b);
        if (l) l.textContent = !variant ? MH.strings.unavailable : variant.available ? MH.strings.addToCart : MH.strings.soldOut;
      });
    };

    root.addEventListener('change', (e) => {
      if (!e.target.closest('[data-options]')) return;
      const sel = selected();
      fieldsets.forEach((fs, i) => { const s = $('[data-selected]', fs); if (s) s.textContent = '— ' + sel[i]; });
      const variant = variants.find((v) => v.options.every((o, i) => o === sel[i]));
      markAvailability(sel);
      setButtons(variant);
      if (!variant) return;
      idInput.value = variant.id;
      const price = $('[data-price] .price', root);
      if (price) {
        price.classList.toggle('price--sale', !!variant.compare);
        price.innerHTML = `<span class="price__now">${variant.price}</span>${variant.compare ? `<s>${variant.compare}</s>` : ''}`;
      }
      const sp = $('[data-sticky-price]', root); if (sp) sp.textContent = variant.price;
      const sv = $('[data-sticky-variant]', root); if (sv) sv.textContent = sel.join(' / ');
      const stock = $('[data-stock]', root);
      if (stock) {
        stock.classList.toggle('is-out', !variant.available);
        $('[data-stock-in]', stock).hidden = !variant.available;
        $('[data-stock-out]', stock).hidden = variant.available;
      }
      showMedia(variant.media);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    });
    if (fieldsets.length) markAvailability(selected());

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (errorEl) errorEl.hidden = true;
      const fd = new FormData(form);
      fd.append('sections', 'cart-drawer');
      fd.append('sections_url', window.location.pathname);
      addBtns.forEach((b) => b.setAttribute('aria-busy', 'true'));
      try {
        const res = await fetch(`${MH.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || MH.strings.cartError);
        const count = renderDrawer(data.sections && data.sections['cart-drawer']);
        if (typeof count === 'number') document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count, source: 'product-form' } }));
        openDrawer();
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message || MH.strings.cartError; errorEl.hidden = false; }
      } finally {
        addBtns.forEach((b) => b.removeAttribute('aria-busy'));
      }
    });

    const buy = $('[data-buy]', root), sticky = $('[data-sticky-buy]', root);
    if (buy && sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', String(!show));
        $$('button', sticky).forEach((b) => (b.tabIndex = show ? 0 : -1));
      }).observe(buy);
    }
  }

  /* ---------- Cart page ---------- */
  function initCart() {
    const getRoot = () => $('[data-cart]');
    if (!getRoot()) return;

    const change = async (line, quantity) => {
      const root = getRoot();
      const sectionId = root.dataset.sectionId;
      root.classList.add('is-busy');
      try {
        const res = await fetch(`${MH.routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: [sectionId, 'cart-drawer'], sections_url: window.location.pathname })
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || MH.strings.cartError);
        const next = parse(data.sections[sectionId]).querySelector('[data-cart]');
        if (next) root.replaceWith(next);
        renderDrawer(data.sections['cart-drawer']);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count: data.item_count, source: 'cart' } }));
      } catch (err) {
        const r = getRoot();
        r.classList.remove('is-busy');
        const el = $('[data-cart-error]', r);
        if (el) { el.textContent = err.message; el.hidden = false; }
      }
    };

    const queued = {};
    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-qty]');
      if (!input || !getRoot().contains(input)) return;
      const line = parseInt(input.dataset.lineQty, 10);
      clearTimeout(queued[line]);
      queued[line] = setTimeout(() => change(line, Math.max(0, parseInt(input.value, 10) || 0)), 350);
    });
    document.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-remove]');
      if (!rm || !getRoot().contains(rm)) return;
      e.preventDefault();
      change(parseInt(rm.dataset.remove, 10), 0);
    });
    document.addEventListener('input', debounce((e) => {
      if (!e.target.matches('[data-cart-note]')) return;
      fetch(`${MH.routes.cartUpdate}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: e.target.value }) });
    }, 400));
  }

  /* ---------- Sticky header height: the visible part of the stuck header group (its height minus the utility bar
     that scrolls away, i.e. height + computed negative top). Published as --sticky-h so pinned panels and
     scroll-margin-top start right below it, whatever the utility bar wraps to. ---------- */
  function initStickyHeight() {
    const sh = $('.section-header') || $('[data-header]');
    if (!sh) return;
    const set = () => {
      const top = parseFloat(getComputedStyle(sh).top) || 0;
      const h = Math.max(0, Math.ceil(sh.getBoundingClientRect().height + Math.min(0, top)));
      if (h) document.documentElement.style.setProperty('--sticky-h', h + 'px');
    };
    set();
    if ('ResizeObserver' in window) new ResizeObserver(set).observe(sh);
    window.addEventListener('resize', debounce(set, 100));
  }

  /* ---------- Core system: scroll progress --p (0..1) over the pinned track. The cards' drop-in, tilt,
     shift and peek are all computed in CSS from --p (desktop and phones). Reduced motion shows the end state. */
  function initCore() {
    const secs = $$('[data-core]');
    if (!secs.length) return;
    // Phones: tags are 270px in the design; grow them to the tallest text (40px top + 96px photo + 8px gap + text + 8px).
    const phoneMQ = window.matchMedia('(max-width: 719px)');
    const sizeTags = () => secs.forEach((el) => {
      const stage = $('.core__stage', el); if (!stage) return;
      if (!phoneMQ.matches) { stage.style.removeProperty('--card-h'); return; }
      const need = Math.max(0, ...$$('.core__txt', el).map((t) => t.scrollHeight));
      stage.style.setProperty('--card-h', Math.max(270, Math.ceil(152 + need)) + 'px');
    });
    sizeTags();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sizeTags);
    window.addEventListener('resize', debounce(sizeTags, 150));
    let ticking = false;
    const update = () => {
      ticking = false;
      secs.forEach((el) => {
        if (reduceMotion.matches) { el.style.setProperty('--p', 1); return; }
        const r = ($('[data-core-track]', el) || el).getBoundingClientRect();
        const ih = window.innerHeight, lead = ih * 0.85;
        const p = Math.max(0, Math.min(1, (lead - r.top) / ((r.height - ih + lead) || 1)));
        // Oct 3: ease toward the scroll target (≈140ms exponential smoothing) instead of jumping per wheel notch.
        el._pT = p;
        if (el._pCur == null) { el._pCur = p; el.style.setProperty('--p', p.toFixed(4)); return; }
        if (!el._pRaf) { el._pLast = 0; el._pRaf = requestAnimationFrame(function tick(t) {
          const dt = el._pLast ? Math.min(64, t - el._pLast) : 16; el._pLast = t;
          const k = 1 - Math.exp(-dt / 140);
          el._pCur += (el._pT - el._pCur) * k;
          const done = Math.abs(el._pT - el._pCur) < 0.0008; if (done) el._pCur = el._pT;
          el.style.setProperty('--p', el._pCur.toFixed(4));
          el._pRaf = done ? 0 : requestAnimationFrame(tick);
        }); }
      });
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    reduceMotion.addEventListener?.('change', onScroll);
    update();
  }

  /* ---------- 04b Curated goods ring ----------
     Orbit (≥720px): the circles turn clockwise, 1 turn / 45s, on the section's ellipse (rAF updates left/top).
     It pauses while a bubble is open, off screen, or in a hidden tab; a click on the ring area (not a circle,
     link or button) stops / resumes it. Phones keep static rows.
     Bubble: mouse hover or a finger tap on a circle opens its photo as a soap bubble centred on screen. It pops
     after 3s, or on a click / tap anywhere outside it (ignored for the first 250ms). One bubble at a time.
     Pointer events, so tablets without a mouse work. Reduced motion: no orbit, no wobble, no fly / splash.
     The dog's eyes follow the pointer; on touch they sweep with scroll. */
  let ringBubble = null, ringMouse = { x: -1, y: -1 }, ringPopAt = null, ringPopTime = 0;
  window.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') ringMouse = { x: e.clientX, y: e.clientY }; }, { passive: true });
  function openBubble(orb) {
    if (ringBubble) return;
    const src = orb.dataset.full || (orb.querySelector('img') || {}).currentSrc;
    if (!src) return;
    const rm = reduceMotion.matches, item = orb.parentElement;
    const r = orb.getBoundingClientRect(), vw = window.innerWidth, vh = window.innerHeight, phone = vw < 720;
    const D = phone ? Math.min(vw, vh) * 0.96 : Math.min(vw * 0.86, vh * 0.86, 900);
    const cx = vw / 2, cy = vh / 2, R = D / 2;
    const ov = document.createElement('div');
    ov.className = 'mh-bubble';
    ov.setAttribute('aria-hidden', 'true');
    const ball = document.createElement('div');
    ball.className = 'mh-bubble__ball';
    ball.style.cssText = `left:${cx - R}px; top:${cy - R}px; width:${D}px; height:${D}px;`;
    ball.innerHTML = '<span class="mh-bubble__film"></span><span class="mh-bubble__shine"></span><span class="mh-bubble__shine mh-bubble__shine--2"></span>';
    const im = new Image();
    im.alt = ''; im.decoding = 'async';
    // Show the circle's already-loaded photo at once, then swap in the large one.
    const small = (orb.querySelector('img') || {}).currentSrc;
    im.src = small || src;
    if (small && small !== src) { const big = new Image(); big.onload = () => { im.src = src; }; big.src = src; }
    ball.prepend(im);
    ov.appendChild(ball);
    document.body.appendChild(ov);
    item.classList.add('is-out');
    const openedAt = Date.now();
    const state = { ov, item, pop: null, t: 0 };
    ringBubble = state;
    requestAnimationFrame(() => ov.classList.add('is-dim'));
    if (!rm && ball.animate) {
      const k = r.width / D, dx = (r.left + r.width / 2) - cx, dy = (r.top + r.height / 2) - cy;
      // Oct 3: grows over 1.8s, then wobbles while it stays (3.5s).
      ball.animate([{ transform: `translate(${dx}px, ${dy}px) scale(${k})` }, { transform: 'scale(1.02)', offset: .8 }, { transform: 'scale(1)' }],
        { duration: 1800, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' });
      ball.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.015, .99)' }, { transform: 'scale(.99, 1.012)' }, { transform: 'scale(1)' }],
        { duration: 1600, delay: 1800, iterations: 2, easing: 'ease-in-out' });
    }
    const outside = (e) => {
      if (Date.now() - openedAt < 250) return;
      if (ball.contains(e.target)) return;
      pop();
    };
    const onKey = (e) => { if (e.key === 'Escape') pop(); };
    const pop = () => {
      if (state.popped) return;
      state.popped = true;
      clearTimeout(state.t);
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', onKey);
      ov.classList.remove('is-dim');
      ringPopAt = { x: ringMouse.x, y: ringMouse.y };
      ringPopTime = Date.now();
      const done = () => { ov.remove(); item.classList.remove('is-out'); if (ringBubble === state) ringBubble = null; };
      if (rm || !ball.animate) { done(); return; }
      ball.getAnimations().forEach((a) => a.cancel());
      ball.animate([{ transform: 'scale(1)', opacity: 1 }, { transform: 'scale(1.12)', opacity: .9, offset: .45 }, { transform: 'scale(1.22)', opacity: 0 }],
        { duration: 260, easing: 'cubic-bezier(.3,0,.6,1)', fill: 'forwards' });
      const rim = document.createElement('div');
      rim.className = 'mh-bubble__rim';
      rim.style.cssText = `left:${cx - R}px; top:${cy - R}px; width:${D}px; height:${D}px;`;
      ov.appendChild(rim);
      rim.animate([{ transform: 'scale(1)', opacity: .9 }, { transform: 'scale(1.18)', opacity: 0 }], { duration: 300, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' });
      // ~14 white droplets splash out from the rim and fall.
      const n = 14;
      for (let i = 0; i < n; i++) {
        const d = document.createElement('div'), ang = i / n * Math.PI * 2 + (Math.random() - .5) * .4;
        const sz = 4 + Math.random() * 9, far = 40 + Math.random() * (phone ? 90 : 160), fall = 60 + Math.random() * 140;
        const streak = Math.random() < .45, rot = (ang * 180 / Math.PI + 90) + 'deg';
        const ex = Math.cos(ang) * far, ey = Math.sin(ang) * far * .8;
        d.className = 'mh-bubble__drop';
        d.style.cssText = `left:${cx + Math.cos(ang) * R - sz / 2}px; top:${cy + Math.sin(ang) * R - sz / 2}px; width:${sz}px; height:${streak ? sz * 2.2 : sz}px;`;
        ov.appendChild(d);
        d.animate([
          { transform: `translate(0,0) rotate(${rot}) scale(1)`, opacity: 1 },
          { transform: `translate(${ex * .75}px, ${ey * .75}px) rotate(${rot}) scale(.9)`, opacity: .95, offset: .45 },
          { transform: `translate(${ex}px, ${ey + fall}px) rotate(${rot}) scale(.35)`, opacity: 0 }
        ], { duration: 520 + Math.random() * 260, easing: 'cubic-bezier(.15,.6,.4,1)', fill: 'forwards' });
      }
      setTimeout(done, 820);
    };
    state.pop = pop;
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', onKey);
    state.t = setTimeout(pop, 5300); // 1.8s grow + 3.5s open
  }

  function initRing() {
    $$('[data-ring]').forEach((root) => {
      if (root.dataset.ready) return;
      root.dataset.ready = '1';
      const narrowMQ = window.matchMedia('(max-width: 719px)'), bigMQ = window.matchMedia('(min-width: 1400px)');
      const dog = $('svg[data-eyes]', root);
      const pupils = dog ? $$('[data-pupil]', dog) : [];
      const stage = $('[data-ring-stage]', root);
      const items = $$('[data-spin]', root);
      const orbs = $$('[data-orbit]', root);
      // Eye centres + max travel come from the SVG (data-vw / data-eyes / data-m: the dog button is 495×520, travel 5).
      const lookAt = (x, y) => {
        if (!dog) return;
        const r = dog.getBoundingClientRect(), k = r.width / (+dog.dataset.vw || 150), E = (dog.dataset.eyes || '62,50,88,50').split(',').map(Number), mx = +dog.dataset.m || 4;
        pupils.forEach((g, i) => {
          const ex = r.left + E[i * 2] * k, ey = r.top + E[i * 2 + 1] * k, dx = x - ex, dy = y - ey;
          const d = Math.hypot(dx, dy) || 1, m = Math.min(1, d / 90) * mx;
          g.setAttribute('transform', `translate(${(dx / d * m).toFixed(2)} ${(dy / d * m).toFixed(2)})`);
        });
      };

      /* Orbit. Phones: "Oval spin" – a tall oval (rx 40%, ry 43%), circles scaled .56 at the sides → 1 at top / bottom. */
      let rot = 0, last = 0, off = false, visible = false, raf = 0;
      const place = () => {
        const oval = narrowMQ.matches, big = bigMQ.matches;
        const rx = oval ? 40 : big ? 38 : 40, ry = oval ? 43 : big ? 35.5 : 40, n = items.length || 1;
        items.forEach((el, i) => {
          const a = (-90 + i * 360 / n + rot) * Math.PI / 180;
          el.style.left = (50 + rx * Math.cos(a)).toFixed(3) + '%';
          el.style.top = (50 + ry * Math.sin(a)).toFixed(3) + '%';
          el.style.transform = oval ? `translate(-50%, -50%) scale(${(0.56 + 0.44 * Math.abs(Math.sin(a))).toFixed(3)})` : '';
        });
      };
      const loop = (t) => {
        raf = 0;
        if (!visible || off || reduceMotion.matches || document.hidden) { last = 0; return; }
        raf = requestAnimationFrame(loop);
        if (ringBubble) { last = t; return; }
        rot = (rot + (last ? t - last : 0) * 360 / 45000) % 360;
        last = t;
        place();
      };
      const kick = () => { if (!raf) raf = requestAnimationFrame(loop); };
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((ents) => { visible = ents.some((x) => x.isIntersecting); if (visible) kick(); }).observe(stage);
      } else { visible = true; }
      document.addEventListener('visibilitychange', kick);
      bigMQ.addEventListener?.('change', () => { if (!narrowMQ.matches) place(); });
      narrowMQ.addEventListener?.('change', () => { if (reduceMotion.matches) items.forEach((el) => { el.style.left = el.style.top = el.style.transform = ''; }); else place(); kick(); });
      reduceMotion.addEventListener?.('change', kick);
      if (items.length !== 7) place();
      kick();
      stage.addEventListener('click', (e) => {
        if (e.target.closest('a, button, [data-orbit]')) return;
        // A click outside a bubble pops it – that click must not also stop / start the orbit.
        if (ringBubble || Date.now() - ringPopTime < 600) return;
        off = !off;
        kick();
      });

      /* Bubble: mouse hover, finger tap, or Enter / Space on a focused circle. */
      orbs.forEach((orb) => {
        let down = null;
        // Mouse: the pointer must rest 1s on a circle before its photo opens (cancelled if it leaves). Touch opens at once.
        let hov = 0;
        orb.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          // A popped bubble uncovers the circle under a resting mouse (or the orbit slides one under it): wait for a real move.
          if (ringPopAt && Math.hypot(e.clientX - ringPopAt.x, e.clientY - ringPopAt.y) < 6) return;
          clearTimeout(hov);
          hov = setTimeout(() => openBubble(orb), 1000);
        });
        orb.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') clearTimeout(hov); });
        orb.addEventListener('pointerdown', (e) => { if (e.pointerType !== 'mouse') down = { x: e.clientX, y: e.clientY, t: Date.now() }; });
        orb.addEventListener('pointerup', (e) => {
          if (e.pointerType === 'mouse' || !down) return;
          const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
          down = null;
          if (moved < 12) openBubble(orb);
        });
        orb.addEventListener('pointercancel', () => { down = null; });
        // Keyboard (click with no pointer) and mouse clicks on an already-closed circle.
        orb.addEventListener('click', (e) => { if (e.detail === 0 || (e.pointerType === 'mouse')) openBubble(orb); });
      });

      /* Dog eyes: follow the mouse, and any finger or pen (touchstart / touchmove / pointerdown), on every device:
         not gated on (hover: hover), so tablets get it too. With no touch in the last 1.5s and no mouse, they sweep with scroll. */
      let lastPoke = 0, mouseSeen = false;
      const poke = (x, y) => { lastPoke = Date.now(); lookAt(x, y); };
      window.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') { mouseSeen = true; lookAt(e.clientX, e.clientY); } else poke(e.clientX, e.clientY); }, { passive: true });
      window.addEventListener('pointerdown', (e) => poke(e.clientX, e.clientY), { passive: true });
      const onTouch = (e) => { const t = e.touches && e.touches[0]; if (t) poke(t.clientX, t.clientY); };
      window.addEventListener('touchstart', onTouch, { passive: true });
      window.addEventListener('touchmove', onTouch, { passive: true });
      window.addEventListener('scroll', () => {
        if (mouseSeen || Date.now() - lastPoke < 1500) return;
        const r = stage.getBoundingClientRect(), q = (window.innerHeight - r.top) / (window.innerHeight + r.height);
        if (q < 0 || q > 1) return;
        const a = -Math.PI / 2 + q * Math.PI * 2.4;
        lookAt(r.left + r.width / 2 + Math.cos(a) * r.width * .45, r.top + r.height / 2 + Math.sin(a) * r.height * .45);
      }, { passive: true });
    });
  }

  /* ---------- Dog-bone buttons outside the ring (intro, Our Story CTA): pupils follow the mouse; on touch they follow
     the finger, else sweep with scroll. Eye centres / travel come from the SVG (data-vw, data-eyes, data-m). ---------- */
  function initDogEyes() {
    $$('svg[data-eyes]').forEach((dog) => {
      if (dog.closest('[data-ring]') || dog.dataset.ready) return;
      dog.dataset.ready = '1';
      const pupils = $$('[data-pupil]', dog), E = dog.dataset.eyes.split(',').map(Number), mx = +dog.dataset.m || 5;
      const lookAt = (x, y) => {
        const r = dog.getBoundingClientRect(); if (!r.width) return;
        const k = r.width / (+dog.dataset.vw || 495);
        pupils.forEach((g, i) => {
          const ex = r.left + E[i * 2] * k, ey = r.top + E[i * 2 + 1] * k, dx = x - ex, dy = y - ey;
          const d = Math.hypot(dx, dy) || 1, m = Math.min(1, d / 90) * mx;
          g.setAttribute('transform', `translate(${(dx / d * m).toFixed(2)} ${(dy / d * m).toFixed(2)})`);
        });
      };
      let lastPoke = 0, mouseSeen = false;
      const poke = (x, y) => { lastPoke = Date.now(); lookAt(x, y); };
      window.addEventListener('pointermove', (e) => { if (e.pointerType === 'mouse') { mouseSeen = true; lookAt(e.clientX, e.clientY); } else poke(e.clientX, e.clientY); }, { passive: true });
      window.addEventListener('pointerdown', (e) => poke(e.clientX, e.clientY), { passive: true });
      const onTouch = (e) => { const t = e.touches && e.touches[0]; if (t) poke(t.clientX, t.clientY); };
      window.addEventListener('touchstart', onTouch, { passive: true });
      window.addEventListener('touchmove', onTouch, { passive: true });
      window.addEventListener('scroll', () => {
        if (mouseSeen || Date.now() - lastPoke < 1500) return;
        const r = dog.getBoundingClientRect(), q = (window.innerHeight - r.top) / (window.innerHeight + r.height);
        if (q < 0 || q > 1) return;
        const a = -Math.PI / 2 + q * Math.PI * 2.4;
        lookAt(r.left + r.width / 2 + Math.cos(a) * 160, r.top + r.height / 2 + Math.sin(a) * 120);
      }, { passive: true });
    });
  }

  /* ---------- Our Story origami (ported from layoutFold()/fold() in Brand Concept.dc.html) ----------
     All sheets get the tallest content's height; the pinned section is one segment per fold. Each sheet
     folds from -179.5deg to flat as you scroll, its shade fading, and the stack slides up by one sheet.
     With reduced motion, or when a sheet can't fit the screen, the sheets lie flat (CSS .is-flat). */
  function initOrigami() {
    $$('[data-origami]').forEach((sec) => {
      if (sec.dataset.ready) return;
      sec.dataset.ready = '1';
      const stage = $('[data-ori-stage]', sec), sheets = $('[data-ori-sheets]', sec);
      const folds = $$('[data-fold]', sec), dots = $$('[data-dot]', sec), hint = $('[data-hint]', sec);
      const NF = folds.length;
      let H = 0, seg = 0, flat = false;
      const clear = () => {
        folds.forEach((el) => { el.style.visibility = ''; el.style.transform = ''; });
        sheets.style.transform = ''; sheets.style.top = ''; sec.style.height = '';
      };
      const fold = () => {
        if (flat || !seg) return;
        const P = Math.max(0, (stage.getBoundingClientRect().top - sec.getBoundingClientRect().top) / seg);
        let Y = 0;
        folds.forEach((el, i) => {
          if (!i) return;
          const raw = P - (i - 1), p = Math.min(1, Math.max(0, raw / 0.8)), e = p < .5 ? 2 * p * p : 1 - 2 * (1 - p) * (1 - p);
          el.style.visibility = raw > 0 ? 'visible' : 'hidden';
          el.style.transform = `rotateX(${(-179.5 * (1 - e)).toFixed(2)}deg)`;
          const shade = el.querySelector(':scope > [data-shade]');
          if (shade) shade.style.opacity = (1 - e).toFixed(3);
          Y += e;
        });
        sheets.style.transform = `translateY(${(-Y * H).toFixed(1)}px)`;
        const cur = Math.round(Y);
        dots.forEach((d, i) => { d.classList.toggle('is-on', i <= cur); d.classList.toggle('is-cur', i === cur); });
        if (hint) hint.style.opacity = Y > NF - 1.2 ? 0 : 1;
      };
      const layout = () => {
        sec.classList.remove('is-flat'); flat = false;
        clear();
        if (reduceMotion.matches) { sec.classList.add('is-flat'); flat = true; return; }
        sheets.style.setProperty('--fh', 'auto');
        const sH = stage.clientHeight;
        let m = 0; $$('[data-content]', sec).forEach((c) => { m = Math.max(m, c.offsetHeight); });
        H = Math.ceil(Math.max(m, Math.min(sH - 110, 520)));
        if (H > sH - 24) { sheets.style.removeProperty('--fh'); sec.classList.add('is-flat'); flat = true; return; }
        sheets.style.setProperty('--fh', `${H}px`);
        sheets.style.top = `${Math.max(12, (sH - H) / 2)}px`;
        seg = Math.max(360, sH * .75);
        sec.style.height = `${sH + seg * (NF - 1 + .4)}px`;
        fold();
      };
      let raf = 0;
      window.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(() => { raf = 0; fold(); }); }, { passive: true });
      window.addEventListener('resize', debounce(layout, 150));
      reduceMotion.addEventListener?.('change', layout);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(layout);
      layout();
    });
  }

  /* ---------- Product recommendations ---------- */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(async (el) => {
      try {
        const res = await fetch(el.dataset.recommendations);
        const next = parse(await res.text()).querySelector('[data-recommendations]');
        if (next && next.innerHTML.trim()) { el.innerHTML = next.innerHTML; initReveal(); }
      } catch (e) { /* recommendations are optional */ }
    });
  }

  /* ---------- Shared Ajax add to cart (studio, quick add) ---------- */
  async function ajaxAdd(fd) {
    fd.append('sections', 'cart-drawer');
    fd.append('sections_url', window.location.pathname);
    const res = await fetch(`${MH.routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || MH.strings.cartError);
    const count = renderDrawer(data.sections && data.sections['cart-drawer']);
    if (typeof count === 'number') document.dispatchEvent(new CustomEvent('cart:updated', { detail: { count, source: 'ajax-add' } }));
    openDrawer();
  }

  /* ---------- Custom Studio personalizer: live preview + add with properties ---------- */
  function initStudio(root) {
    const form = $('[data-studio-form]', root);
    if (!form || form.dataset.ready) return;
    form.dataset.ready = '1';
    const plate = $('[data-studio-plate]', root);
    const nameOut = $('[data-studio-name]', root), line2Out = $('[data-studio-line2]', root);
    const variantInput = $('[data-studio-variant]', form);
    const errorEl = $('[data-form-error]', form);
    const fallbackName = nameOut ? nameOut.textContent : '';
    const sync = () => {
      const keep = $('input[name="properties[Keepsake]"]:checked', form);
      const font = $('input[data-font]:checked', form), finish = $('input[data-finish]:checked', form);
      const name = $('[data-studio-input="name"]', form).value.trim();
      const line2 = $('[data-studio-input="line2"]', form).value.trim();
      if (nameOut) nameOut.textContent = name || fallbackName;
      if (line2Out) { line2Out.textContent = line2; line2Out.hidden = !line2; }
      if (plate && font) plate.dataset.font = font.dataset.font;
      if (plate && finish) plate.dataset.finish = finish.dataset.finish;
      if (!keep) return;
      $$('[data-studio-title]', root).forEach((el) => { el.textContent = keep.value; });
      const price = $('[data-studio-price]', root);
      if (price) price.textContent = keep.dataset.price;
      variantInput.value = keep.dataset.variant || '';
      const canAdd = !!keep.dataset.variant;
      $('[data-studio-add]', form).hidden = !canAdd;
      $('[data-studio-request]', form).hidden = canAdd;
    };
    form.addEventListener('input', sync);
    form.addEventListener('change', sync);
    sync();
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (errorEl) errorEl.hidden = true;
      if (!variantInput.value) {
        const url = new URL(root.dataset.requestUrl || ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'pages/contact', window.location.origin);
        window.location.href = url.toString();
        return;
      }
      if (!form.reportValidity()) return;
      const btn = $('[data-studio-submit]', form);
      btn.setAttribute('aria-busy', 'true');
      try {
        await ajaxAdd(new FormData(form));
      } catch (err) {
        if (errorEl) { errorEl.textContent = err.message || MH.strings.cartError; errorEl.hidden = false; }
      } finally {
        btn.removeAttribute('aria-busy');
      }
    });
  }

  /* ---------- Fan Zone votes (placeholder: remembered in this browser only) ---------- */
  function initVotes(root) {
    const key = 'mh-votes';
    let votes = {};
    try { votes = JSON.parse(localStorage.getItem(key)) || {}; } catch (e) { votes = {}; }
    const paint = (btn) => {
      const on = !!votes[btn.dataset.voteBtn];
      btn.setAttribute('aria-pressed', String(on));
      $('[data-vote-count]', btn).textContent = parseInt(btn.dataset.votes, 10) + (on ? 1 : 0);
    };
    $$('[data-vote-btn]', root).forEach((btn) => {
      paint(btn);
      if (btn.dataset.ready) return;
      btn.dataset.ready = '1';
      btn.addEventListener('click', () => {
        const id = btn.dataset.voteBtn;
        if (votes[id]) delete votes[id]; else votes[id] = 1;
        try { localStorage.setItem(key, JSON.stringify(votes)); } catch (e) { /* voting still works for this view */ }
        paint(btn);
      });
    });
  }

  /* ---------- Training Hub booking drawer (contact form) ---------- */
  function initBooking() {
    const drawer = $('[data-booking-drawer]');
    if (!drawer || drawer.dataset.ready) return;
    drawer.dataset.ready = '1';
    const opener = $('[data-booking-open]');
    const panel = $('.drawer__panel', drawer);
    const form = drawer.closest('form');
    const open = () => {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      opener && opener.setAttribute('aria-expanded', 'true');
      document.documentElement.style.overflow = 'hidden';
      panel.focus();
    };
    const close = () => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      opener && opener.setAttribute('aria-expanded', 'false');
      document.documentElement.style.overflow = '';
      opener && opener.focus();
    };
    opener && opener.addEventListener('click', open);
    drawer.addEventListener('click', (e) => { if (e.target.closest('[data-booking-close]')) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && drawer.classList.contains('is-open')) close(); });

    // Next 10 days, Sundays skipped, as pill radios (replaces the no-JS date input).
    const days = $('[data-booking-days]', drawer);
    if (days) {
      const lang = document.documentElement.lang || 'en';
      const loc = lang.startsWith('el') ? 'el-GR' : 'en-GB';
      const d0 = new Date(); d0.setHours(12, 0, 0, 0);
      const out = [];
      for (let k = 1; out.length < 10; k++) {
        const d = new Date(d0); d.setDate(d0.getDate() + k);
        if (d.getDay() !== 0) out.push(d);
      }
      days.innerHTML = out.map((d, i) => {
        const long = d.toLocaleDateString(loc, { weekday: 'long', day: 'numeric', month: 'long' });
        return `<label class="booking__day"><input type="radio" name="contact[Date]" value="${long}" required><span><small>${d.toLocaleDateString(loc, { weekday: 'short' })}</small><strong>${d.getDate()}</strong><small>${d.toLocaleDateString(loc, { month: 'short' })}</small></span></label>`;
      }).join('');
    }
    const update = () => {
      const ready = form && $('input[name="contact[Date]"]:checked', form) && $('input[name="contact[Time]"]:checked', form);
      const pick = $('[data-booking-pick]', drawer), confirm = $('[data-booking-confirm]', drawer);
      if (pick && confirm) { pick.hidden = !!ready; confirm.hidden = !ready; }
    };
    if (form) {
      form.addEventListener('change', update);
      form.addEventListener('submit', () => {
        try {
          const v = (n) => (($(`input[name="contact[${n}]"]:checked`, form) || {}).value || '');
          sessionStorage.setItem('mh-booking', JSON.stringify({ type: v('Session'), when: `${v('Date')} · ${v('Time')}` }));
        } catch (e) { /* summary is optional */ }
      });
    }
    update();

    if (drawer.hasAttribute('data-open-on-load')) {
      const summary = $('[data-booking-summary]', drawer);
      try {
        const b = JSON.parse(sessionStorage.getItem('mh-booking'));
        if (b && summary) {
          summary.innerHTML = '<strong></strong><span></span>';
          summary.children[0].textContent = b.type;
          summary.children[1].textContent = b.when;
          summary.hidden = false;
        }
        sessionStorage.removeItem('mh-booking');
      } catch (e) { /* summary is optional */ }
      open();
    }
  }

  /* ---------- Shop (collection) page ----------
     Chips are links (state in the URL). Here: quick add, sort on change, live search over the cards on the page
     (Enter runs the full store search), and the empty state for a pet / type with no products yet: chip links carry
     ?mh_type={tags}; when no product has those tags Shopify 301-redirects to the plain collection keeping the query,
     so if the requested tags aren't applied, show "Nothing here yet" and light up the requested chips. */
  const handle = (t) => String(t).toLowerCase().replace(/[^a-z0-9Ͱ-Ͽ]+/g, '-').replace(/^-+|-+$/g, '');
  function initComingSoon() {
    const root = $('[data-collection]');
    const want = new URLSearchParams(location.search).get('mh_type');
    if (!root || !want) return;
    const cur = (root.dataset.currentTags || '').split(',').filter(Boolean);
    const raw = want.split(/[+ ]/).filter(Boolean);
    const wanted = raw.map(handle);
    if (wanted.every((t) => cur.includes(t))) return;
    root.classList.add('is-soon');
    // The redirect dropped the filter from the path, so rebuild chip links from the requested tags: picking a type
    // keeps the requested pet (and vice versa), e.g. Dogs → Smart = /collections/all/pet:dogs+type:smart.
    const reqTag = (kind) => raw.find((t) => t.toLowerCase().startsWith(kind + ':'));
    const base = (localeRoot === '/' ? '' : localeRoot.slice(0, -1)) + '/collections/all';
    const link = (tags) => { const tg = tags.filter(Boolean); return tg.length ? `${base}/${tg.join('+')}?mh_type=${encodeURIComponent(tg.join('+'))}` : base; };
    $$('[data-chip-row]', root).forEach((row) => {
      const kind = row.dataset.chipRow, other = reqTag(kind === 'pet' ? 'type' : 'pet');
      if (!other) return;
      $$('[data-tag]', row).forEach((a) => {
        const own = a.dataset.tag === 'all' ? null : a.dataset.tag.replace(/^(pet|type)-/, '$1:');
        a.setAttribute('href', link(kind === 'pet' ? [own, other] : [other, own]));
      });
    });
    const soon = $('[data-coll-soon]', root); if (soon) soon.hidden = false;
    $$('[data-chip-row]', root).forEach((row) => {
      const mine = wanted.filter((t) => t.startsWith(row.dataset.chipRow + '-'));
      if (!mine.length) return;
      $$('[data-tag]', row).forEach((a) => {
        const on = mine.includes(a.dataset.tag);
        a.classList.toggle('is-on', on);
        if (on) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
      });
    });
  }

  function initCollection() {
    const root = $('[data-collection]');
    if (!root || root.dataset.ready) return;
    root.dataset.ready = '1';
    root.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      const fd = new FormData();
      fd.append('id', btn.dataset.quickAdd);
      fd.append('quantity', '1');
      btn.setAttribute('aria-busy', 'true');
      try { await ajaxAdd(fd); } catch (err) { window.alert(err.message || MH.strings.cartError); }
      finally { btn.removeAttribute('aria-busy'); }
    });
    const sort = $('[data-sort-select]', root);
    sort && sort.addEventListener('change', () => sort.form.submit());
    const input = $('[data-coll-search]', root), grid = $('[data-coll-grid]', root), countEl = $('[data-count-n]', root), soon = $('[data-coll-soon]', root);
    if (!input || !grid) return;
    const items = $$('.cg__item', grid), count0 = countEl ? countEl.textContent : '';
    const fmt = (n) => { const c = $('[data-coll-count]', root); const tpl = n === 1 ? c.dataset.one : c.dataset.other; return tpl.replace(/\d+/, String(n)); };
    input.addEventListener('input', debounce(() => {
      const q = input.value.trim().toLowerCase();
      let n = 0;
      items.forEach((it) => { const show = !q || it.dataset.search.includes(q); it.hidden = !show; if (show) n += 1; });
      if (countEl) countEl.textContent = q ? fmt(n) : count0;
      if (soon && !root.classList.contains('is-soon')) soon.hidden = n > 0;
      const pill = input.closest('[data-search-bird]'); if (pill) pill.toggleAttribute('data-none', !!q && n === 0);
    }, 120));
  }

  /* ---------- Form success messages: bring them into view after the post reloads the page ---------- */
  function initFormFocus() {
    const el = $('[data-focus-on-load]');
    if (!el || el.closest('[data-booking-drawer]')) return;
    el.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    el.focus({ preventScroll: true });
  }

  /* ---------- Paw cursor + sparks (desktop pointer, motion allowed) ---------- */
  function initPaw() {
    if (!MH.paw || reduceMotion.matches || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const paw = document.createElement('div');
    paw.setAttribute('aria-hidden', 'true');
    paw.innerHTML = '<svg viewBox="0 0 24 24" width="17" height="17"><g fill="#1A1A1A" stroke="#fff" stroke-width="1.2" stroke-linejoin="round"><ellipse cx="5.2" cy="10.2" rx="2.1" ry="2.6" transform="rotate(-18 5.2 10.2)"/><ellipse cx="9.4" cy="5.6" rx="2.2" ry="2.8" transform="rotate(-6 9.4 5.6)"/><ellipse cx="14.6" cy="5.6" rx="2.2" ry="2.8" transform="rotate(6 14.6 5.6)"/><ellipse cx="18.8" cy="10.2" rx="2.1" ry="2.6" transform="rotate(18 18.8 10.2)"/><path d="M12 10.6c-2.6 0-5.6 3.6-5.6 6.4 0 2 1.5 3.2 3.2 3.2 1 0 1.6-.5 2.4-.5s1.4.5 2.4.5c1.7 0 3.2-1.2 3.2-3.2 0-2.8-3-6.4-5.6-6.4z"/></g></svg>';
    Object.assign(paw.style, { position: 'fixed', left: '0', top: '0', zIndex: '2147483646', pointerEvents: 'none', opacity: '0', transition: 'opacity .2s cubic-bezier(.2,.7,.2,1)', willChange: 'transform', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.18))' });
    document.body.appendChild(paw);
    let tx = -100, ty = -100, x = -100, y = -100, press = 1, raf = 0;
    const loop = () => {
      x += (tx - x) * 0.75; y += (ty - y) * 0.75; press += (1 - press) * 0.22;
      const lean = Math.max(-6, Math.min(6, (tx - x) * 0.8));
      paw.style.transform = `translate(${x + 7}px,${y + 11}px) rotate(${-24 + lean}deg) scale(${press})`;
      raf = Math.abs(tx - x) + Math.abs(ty - y) > 0.3 || Math.abs(1 - press) > 0.01 ? requestAnimationFrame(loop) : 0;
    };
    const kick = () => { if (!raf) raf = requestAnimationFrame(loop); };
    window.addEventListener('mousemove', (e) => { tx = e.clientX; ty = e.clientY; if (x < -50) { x = tx; y = ty; } paw.style.opacity = '1'; kick(); }, { passive: true });
    document.addEventListener('mouseleave', () => { paw.style.opacity = '0'; });
    const COLORS = ['#FDC20E', '#D8B72A', '#8A9638', '#4BAE84', '#27C6D8'];
    window.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') return;
      press = 0.72; kick();
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 + Math.random() * 0.5, d = 18 + Math.random() * 18, len = 5 + Math.random() * 5, deg = a * 180 / Math.PI;
        const sp = document.createElement('span');
        Object.assign(sp.style, { position: 'fixed', left: e.clientX + 12 + 'px', top: e.clientY + 16 + 'px', width: len + 'px', height: '2.5px', marginTop: '-1.25px', borderRadius: '2px', background: COLORS[i % COLORS.length], pointerEvents: 'none', zIndex: '2147483647', transformOrigin: '0 50%' });
        document.body.appendChild(sp);
        sp.animate([
          { transform: `rotate(${deg}deg) translateX(4px) scaleX(.4)`, opacity: 1 },
          { transform: `rotate(${deg}deg) translateX(${d}px) scaleX(1)`, opacity: 1, offset: 0.55 },
          { transform: `rotate(${deg}deg) translateX(${d + 6}px) scaleX(.2)`, opacity: 0 }
        ], { duration: 460 + Math.random() * 120, easing: 'cubic-bezier(.2,.7,.2,1)' }).onfinish = () => sp.remove();
      }
    }, { passive: true });
  }

  /* ---------- Keep the language: on /el (any non-default language) every internal link keeps the prefix.
     Liquid already builds links with routes.* / the local-url snippet; this catches anything else (HTML from
     Ajax, menus, rich text) on load and again at click time. Language switch links are left alone. ---------- */
  const localeRoot = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || MH.routes.root || '/').replace(/\/?$/, '/');
  const SKIP_PATH = /^\/(cdn|checkout|checkouts|services|apps|tools|wpm|web-pixels|admin|password|challenge)(\/|$)/;
  function localizeLink(a) {
    if (localeRoot === '/' || !a || a.hasAttribute('data-lang-link') || a.hasAttribute('download') || a.closest('[data-lang-link], .lang')) return;
    const raw = a.getAttribute('href');
    if (!raw || raw.charAt(0) !== '/' || raw.charAt(1) === '/') return;
    const bare = localeRoot.slice(0, -1); // "/el"
    if (raw === bare || raw.startsWith(localeRoot) || raw.startsWith(bare + '?') || raw.startsWith(bare + '#')) return;
    if (SKIP_PATH.test(raw)) return;
    a.setAttribute('href', raw === '/' ? localeRoot : bare + raw);
  }
  function initLocaleLinks() {
    if (localeRoot === '/') return;
    $$('a[href^="/"]').forEach(localizeLink);
    document.addEventListener('click', (e) => { const a = e.target.closest && e.target.closest('a[href^="/"]'); if (a) localizeLink(a); }, true);
  }

  /* ---------- Language links keep the current query string (Liquid can't read it) ---------- */
  function initLangLinks() {
    if (!location.search) return;
    $$('[data-lang-link]').forEach((a) => {
      const url = new URL(a.getAttribute('href'), location.href);
      url.search = location.search;
      a.setAttribute('href', url.pathname + url.search);
    });
  }

  const init = () => {
    initLangLinks();
    initLocaleLinks();
    initStickyHeight();
    initReveal();
    initHeroVideo();
    initHeader();
    $$('[data-product]').forEach(initProduct);
    initCart();
    initCore();
    initRing();
    initDogEyes();
    initOrigami();
    initRecommendations();
    $$('[data-studio]').forEach(initStudio);
    $$('[data-vote]').forEach(initVotes);
    initBooking();
    initCollection();
    initBackToShop();
    initComingSoon();
    initFormFocus();
    initPaw();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run section scripts after a section reloads.
  document.addEventListener('shopify:section:load', (e) => {
    initReveal();
    initHeroVideo();
    if (e.target.querySelector('[data-header]')) { initHeader(); initLangLinks(); }
    $$('[data-product]', e.target).forEach(initProduct);
    if (e.target.querySelector('[data-core]')) initCore();
    if (e.target.querySelector('[data-ring]')) { if (ringBubble) ringBubble.pop(); initRing(); }
    if (e.target.querySelector('[data-origami]')) initOrigami();
    $$('[data-studio]', e.target).forEach(initStudio);
    $$('[data-vote]', e.target).forEach(initVotes);
    initBooking();
  });
})();
